import { Box, Heading, Text, TextButton } from '@wix/design-system'
import { Check, Edit } from '@wix/wix-ui-icons-common/lazy'
import React, { FC, Fragment, memo, ReactNode, useCallback, useEffect, useState } from 'react'

import { InCircle } from './InCircle'
import s from './Step.scss'

type StepProps = {
  state: 'default' | 'active' | 'complete'
  index: number
  content: {
    title: string
    doneText: string
  }
  answer?: string
  onClick?: (index: number) => void
  children?: ReactNode
}

export const Step: FC<StepProps> = memo(({ state, index, content, answer, onClick, children }) => {
  const isActive = state === 'active'
  const isComplete = state === 'complete'

  const doneTexts = content.doneText.split(/\$answer/g)

  const handleClick = useCallback(() => onClick?.(index), [onClick, index])

  const [hover, setHover] = useState(false)
  const onMouseEnter = () => {
    setHover(true)
  }
  const onMouseLeave = () => {
    setHover(false)
  }

  useEffect(() => {
    setHover(false)
  }, [state])

  return (
    <>
      <Box gap="18px" verticalAlign="middle" marginTop="35px">
        <InCircle fill={isActive}>
          {isComplete && <Check color="#116DFF" />}
          {!isComplete && (
            <Text light={isActive} weight="thin" skin={isActive ? 'standard' : 'primary'}>
              {index}
            </Text>
          )}
        </InCircle>
        {isComplete && (
          <Box verticalAlign="middle">
            {!!answer && (
              <Text weight="thin" secondary>
                {doneTexts.map((text, index) => (
                  <Fragment key={index}>
                    {text && <span>{text}</span>}
                    {index < doneTexts.length - 1 && (
                      <TextButton
                        onClick={handleClick}
                        underline="always"
                        skin={hover ? 'standard' : 'dark'}
                        suffixIcon={hover ? <Edit /> : undefined}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                      >
                        {answer}
                      </TextButton>
                    )}
                  </Fragment>
                ))}
              </Text>
            )}
            {!answer && (
              <Heading size="medium" className={s.completeText} onClick={handleClick}>
                <Box verticalAlign="middle">
                  {content.title}
                  <Edit />
                </Box>
              </Heading>
            )}
          </Box>
        )}
        {!isComplete && <Heading size="medium">{content.title}</Heading>}
      </Box>
      <Box direction="vertical">
        <Box margin="30px 0 0 32px" display={isActive ? undefined : 'none'} direction="vertical">
          {children}
        </Box>
      </Box>
    </>
  )
})
